import { Component, OnInit, ViewChild, inject, Input, Output, EventEmitter } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatListModule, MatSelectionList } from '@angular/material/list';
import { MatSelectModule } from '@angular/material/select';
import { catchError, firstValueFrom, of, Subscription, switchMap, takeWhile, tap, timer } from 'rxjs';
import { AppGeneralStateService } from 'src/app/app-general-state.service';
import { DataService } from 'src/app/core/services/data/data.service';
import {
  ICompaniesShortList,
  IParkerActiveListForInvoiceGeneration,
  IPrelimInvoiceResponse,
  IGBPrelimFutureRequest,
  IInvoiceSystemGenerationDefaults,
} from 'src/app/shared/interfaces';
import { spaceArCoreHttpApi } from 'src/app/shared/code/libraries/http-data/spaceArHttpApi';

@Component({
  selector: 'app-multi-month-future',
  standalone: true,
  imports: [FormsModule, MatFormFieldModule, MatInputModule, MatSelectModule, MatListModule, MatCheckboxModule],
  templateUrl: './multi-month-future.component.html',
  styleUrl: './multi-month-future.component.scss',
})
export class MultiMonthFutureComponent implements OnInit {
  companyList: ICompaniesShortList[] = [];
  private _companyListData: ICompaniesShortList[] = [];
  parkerList: IParkerActiveListForInvoiceGeneration[] = [];
  selectedMonths: Date | null = null;
  monthOptions: Date[] = [];
  invoiceLineDescription: string = '';
  isInvoiceLineDescriptionCustomizationEnabled: boolean = false;
  selectedCompanyIds: string[] = [];
  currentSelectedCompanyId: string = '';
  isCurrentMonthOnly: boolean = false;
  isSelectAllParkersChecked: boolean = false;
  isParkerSelectionReadOnly: boolean = false;
  isProcessing: boolean = false;
  private hasInitialized: boolean = false;
  private isResetForLotChangeScheduled: boolean = false;
  private isCompanyMultiSelectModifierHeld: boolean = false;
  private lastCancelGenerationToken: number = 0;
  private generationRequestSubscription: Subscription | null = null;
  private pollingSubscription: Subscription | null = null;
  private currentJobId: string | null = null;
  private readonly POLL_INTERVAL_MS = 2000;
  disablePrelimGeneration: boolean = false;
  numberOfMonths: number = 0; // to do replace with lotsetup value
  currentMonth: Date = new Date(new Date().getFullYear(), new Date().getMonth(), 1);
  prelimList: IPrelimInvoiceResponse[] = [];
  invoiceDate: Date;
  dueDate: Date;
  invoiceSystemDefaults: IInvoiceSystemGenerationDefaults = undefined as any;
  companyMultiSelectModifierActive: boolean = false;

  get isMultipleCompaniesSelected(): boolean {
    return this.selectedCompanyIds.length > 1;
  }

  @Input()
  set systemDefaults(defaults: IInvoiceSystemGenerationDefaults) {
    this.invoiceSystemDefaults = defaults;
    this.syncNumberOfMonthsFromDefaults();
    if (defaults && this.hasInitialized) {
      this.scheduleResetForLotChange();
    }
  }

  @Input()
  set resetPrelimButton(_: number) {
    this.disablePrelimGeneration = false;
  }

  @Input()
  set invoiceLineCustomizationStatus(status: boolean) {
    this.isInvoiceLineDescriptionCustomizationEnabled = status;
  }

  @Input()
  set companyListData(companies: ICompaniesShortList[]) {
    this._companyListData = companies ?? [];
    this.companyList = this._companyListData;
  }

  get companyListData(): ICompaniesShortList[] {
    return this._companyListData;
  }

  @Input()
  set lotNumberResetToken(_: number) {
    if (!this.hasInitialized) {
      return;
    }
    this.scheduleResetForLotChange();
  }

  @Input()
  set cancelGenerationToken(token: number) {
    if (!this.hasInitialized || token === this.lastCancelGenerationToken) {
      return;
    }

    this.lastCancelGenerationToken = token;
    this.cancelGeneration();
  }

  @Output() prelimInvoiceListChange = new EventEmitter<IPrelimInvoiceResponse[]>();
  @Output() processingStateChange = new EventEmitter<boolean>();
  @Output() notifyOfErrorDuringInvoiceGeneration = new EventEmitter<string>();

  @ViewChild('parkerSelectionList') parkerSelectionList?: MatSelectionList;

  dataService = inject(DataService);
  appGeneralStateService = inject(AppGeneralStateService);
  spaceArHttpService = inject(spaceArCoreHttpApi);

  ngOnInit(): void {
    this.syncNumberOfMonthsFromDefaults();
    this.onInitNumberOfMonths();
    this.hasInitialized = true;
  }

  ngOnDestroy(): void {
    this.stopPolling();
  }

  onInitCurrentMonth(): void {}

  private syncNumberOfMonthsFromDefaults(): void {
    this.numberOfMonths = this.invoiceSystemDefaults?.futureMonthInvoiceLimit ?? 0;
  }

  onInitNumberOfMonths(): void {
    const now = new Date();
    const firstOfNextMonth = new Date(now.getFullYear(), now.getMonth() + 1, 1);

    this.monthOptions = Array.from({ length: this.numberOfMonths }, (_, index) => {
      return new Date(firstOfNextMonth.getFullYear(), firstOfNextMonth.getMonth() + index, 1);
    });

    this.selectedMonths = this.monthOptions.length > 0 ? this.monthOptions[0] : null;
  }

  formatMonthOption(dateValue: Date): string {
    return `${dateValue.getMonth() + 1}-${dateValue.getDate()}-${dateValue.getFullYear()}`;
  }

  async getParkerList(companyId: string): Promise<void> {
    const startDatePlug: Date = this.currentMonth;
    const endDatePlug: Date = this.selectedMonths ?? this.currentMonth;
    const parkerListTest = await firstValueFrom(
      this.spaceArHttpService.getActiveFutureParkerListByCompanyId(companyId, startDatePlug, endDatePlug),
    );
    this.parkerList = parkerListTest;
  }

  async onCompanySelectionChange(companyIds: string[]): Promise<void> {
    const previousSelection = this.selectedCompanyIds;
    const previousCompanyId = this.currentSelectedCompanyId;

    this.selectedCompanyIds = this.resolveCompanySelection(previousSelection, companyIds);
    this.companyMultiSelectModifierActive = this.isMultipleCompaniesSelected;
    // The first company picked stays the parker anchor until it is deselected.
    this.currentSelectedCompanyId = this.selectedCompanyIds.includes(previousCompanyId)
      ? previousCompanyId
      : (this.selectedCompanyIds[0] ?? '');

    await this.applyCompanySelection(this.currentSelectedCompanyId !== previousCompanyId);
  }

  onCompanyListPointerDown(event: MouseEvent): void {
    this.isCompanyMultiSelectModifierHeld = event.ctrlKey || event.metaKey;
  }

  onCompanyListKeydown(event: KeyboardEvent, companySelectionList: MatSelectionList): void {
    this.isCompanyMultiSelectModifierHeld = event.ctrlKey || event.metaKey;

    // MatSelectionList only wires select-all to Ctrl+A, so mirror it for the macOS Command key.
    if (event.metaKey && !event.ctrlKey && event.key.toLowerCase() === 'a') {
      event.preventDefault();

      if (companySelectionList.options.some((option) => !option.selected)) {
        companySelectionList.selectAll();
      } else {
        companySelectionList.deselectAll();
      }
    }
  }

  // A plain click replaces the selection; Ctrl/Cmd clicks and select-all keep the list's own toggle result.
  private resolveCompanySelection(previousSelection: string[], companyIds: string[]): string[] {
    if (this.isCompanyMultiSelectModifierHeld) {
      return companyIds;
    }

    const added = companyIds.filter((companyId) => !previousSelection.includes(companyId));
    const removed = previousSelection.filter((companyId) => !companyIds.includes(companyId));

    if (added.length + removed.length !== 1) {
      return companyIds;
    }

    return [added[0] ?? removed[0]];
  }

  async onEndMonthChange(): Promise<void> {
    await this.applyCompanySelection(true);
  }

  private async applyCompanySelection(shouldReloadParkers: boolean): Promise<void> {
    if (!this.currentSelectedCompanyId) {
      this.parkerList = [];
      this.parkerSelectionList?.deselectAll();
      this.isSelectAllParkersChecked = false;
      this.isParkerSelectionReadOnly = false;
      return;
    }

    if (shouldReloadParkers) {
      await this.getParkerList(this.currentSelectedCompanyId);
    }

    // With multiple companies the select-all toggle is not offered, but every parker is still included.
    this.isSelectAllParkersChecked = !this.isMultipleCompaniesSelected;

    if (this.parkerList.length === 0) {
      this.isParkerSelectionReadOnly = false;
      return;
    }

    setTimeout(() => {
      this.parkerSelectionList?.selectAll();
      this.isParkerSelectionReadOnly = true;
    });
  }

  onToggleSelectAllParkers(parkerSelectionList: MatSelectionList): void {
    if (this.isSelectAllParkersChecked) {
      parkerSelectionList.selectAll();
      this.isParkerSelectionReadOnly = true;
      return;
    }

    parkerSelectionList.deselectAll();
    this.isParkerSelectionReadOnly = false;
  }

  private resetForLotChange(): void {
    this.parkerList = [];
    this.selectedCompanyIds = [];
    this.companyMultiSelectModifierActive = false;
    this.currentSelectedCompanyId = '';
    this.parkerSelectionList?.deselectAll();
    this.selectedMonths = null;
    this.invoiceLineDescription = '';
    this.isCurrentMonthOnly = false;
    this.isSelectAllParkersChecked = false;
    this.isParkerSelectionReadOnly = false;
    this.disablePrelimGeneration = false;
    this.syncNumberOfMonthsFromDefaults();
    this.onInitNumberOfMonths();
  }

  private scheduleResetForLotChange(): void {
    if (this.isResetForLotChangeScheduled) {
      return;
    }

    this.isResetForLotChangeScheduled = true;
    queueMicrotask(() => {
      this.isResetForLotChangeScheduled = false;
      this.resetForLotChange();
    });
  }

  onGeneratePreliminaryInvoice(): void {
    const invoiceCriteria: IGBPrelimFutureRequest = this.setInvoiceSearchCriteria();

    this.isProcessing = true;
    this.processingStateChange.emit(true);
    this.stopPolling();

    this.generationRequestSubscription?.unsubscribe();
    this.generationRequestSubscription = this.dataService
      .gb_GeneratePrelimInvoiceForFutureMonths(invoiceCriteria)
      .subscribe({
        next: (response) => {
          this.currentJobId = response.jobId;
          this.startPolling(response.jobId);
        },
        error: (error) => {
          this.notifyOfErrorDuringInvoiceGeneration.emit(
            this.getGenerationSubmissionErrorMessage(error),
          );
          this.isProcessing = false;
          this.processingStateChange.emit(false);
          this.generationRequestSubscription = null;
        },
        complete: () => (this.generationRequestSubscription = null),
      });
  }

  private getGenerationSubmissionErrorMessage(error: any): string {
    if (error?.status === 409 && typeof error.error?.message === 'string') {
      return error.error.message;
    }

    return '';
  }

  private startPolling(jobId: string): void {
    this.stopPolling();
    this.pollingSubscription = timer(0, this.POLL_INTERVAL_MS)
      .pipe(
        switchMap(() => this.dataService.GetInvoiceGenerationJobStatus(jobId)),
        tap((status) => this.processingStateChange.emit(status.status === 'Queued' || status.status === 'Processing')),
        takeWhile((status) => status.status === 'Queued' || status.status === 'Processing', true),
        catchError(() => {
          this.notifyOfErrorDuringInvoiceGeneration.emit(
            'Error checking job status. The job may still be processing. Please refresh and check the preliminary invoices list.',
          );
          this.isProcessing = false;
          this.processingStateChange.emit(false);
          this.currentJobId = null;
          return of(null);
        }),
      )
      .subscribe((status) => {
        if (!status) return;
        if (status.status === 'Completed') {
          this.prelimList = status.result ?? [];
          this.prelimInvoiceListChange.emit(this.prelimList);
          this.isProcessing = false;
          this.processingStateChange.emit(false);
          this.disablePrelimGeneration = this.prelimList.length > 0;
          this.currentJobId = null;
        } else if (status.status === 'Failed') {
          this.notifyOfErrorDuringInvoiceGeneration.emit(status.errorMessage ?? 'Future-month invoice generation failed.');
          this.isProcessing = false;
          this.processingStateChange.emit(false);
          this.currentJobId = null;
        } else if (status.status === 'Cancelled') {
          this.isProcessing = false;
          this.processingStateChange.emit(false);
          this.currentJobId = null;
        }
      });
  }

  private stopPolling(): void {
    this.pollingSubscription?.unsubscribe();
    this.pollingSubscription = null;
  }

  cancelGeneration(): void {
    if (!this.isProcessing && !this.generationRequestSubscription) {
      return;
    }

    this.generationRequestSubscription?.unsubscribe();
    this.stopPolling();
    this.generationRequestSubscription = null;
    this.currentJobId = null;
    this.isProcessing = false;
    this.processingStateChange.emit(false);
  }

  setInvoiceSearchCriteria(): IGBPrelimFutureRequest {
    let invoiceCriteria: IGBPrelimFutureRequest = {} as IGBPrelimFutureRequest;
    const selectedMonthIndex = this.selectedMonths
      ? this.monthOptions.findIndex((month) => month.getTime() === this.selectedMonths?.getTime())
      : -1;

    invoiceCriteria.lotNumber = this.appGeneralStateService.currentLotNumber;
    invoiceCriteria.entityIdList = this.selectedCompanyIds.join(',');
    invoiceCriteria.invoiceDate = this.currentMonth;
    invoiceCriteria.dueDate = this.currentMonth;
    invoiceCriteria.numberOfMonths = selectedMonthIndex >= 0 ? selectedMonthIndex + 2 : 1;
    invoiceCriteria.parkerIdList = this.getParkerIdList();
    invoiceCriteria.userName = this.appGeneralStateService.userName;
    invoiceCriteria.gbLineDescription = this.invoiceLineDescription;

    // invoiceCriteria.lotNumber = this.appGeneralStateService.currentLotNumber;
    // invoiceCriteria.entityIdList = '053019164723640';
    // invoiceCriteria.invoiceDate = new Date(2026, 4, 1);
    // invoiceCriteria.dueDate = new Date(2026, 4, 1);
    // invoiceCriteria.numberOfMonths = 6;
    // invoiceCriteria.parkerIdList = '';
    // invoiceCriteria.userName = this.appGeneralStateService.userName;
    // invoiceCriteria.gbLineDescription = 'custom line description';

    return invoiceCriteria;
  }

  // Gets the list of Parkers that are active and should be included in the invoice generation
  getParkerIdList(): string {
    // 20260509: Ken Moss
    // Changed to ALWAYS pass all ParkerPayType = 'P' even if all items are selected.
    const selectedIds = new Set<string>(
      (this.parkerSelectionList?.selectedOptions.selected ?? []).map((option) => option.value),
    );

    return this.parkerList
      .filter((parker) => selectedIds.has(parker.parkerId) && (parker.parkerPayType ?? '').toUpperCase() === 'P')
      .map((parker) => parker.parkerId)
      .join(',');
  }
}
